import React from 'react';
import { DollarSign, Save, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface WithdrawalFormProps {
  onSuccess: () => void;
}

interface User {
  id: string;
  name: string;
  available_balance: number;
}

interface UserSearchResult {
  id: string;
  name: string;
}

export function WithdrawalForm({ onSuccess }: WithdrawalFormProps) {
  const [form, setForm] = React.useState({
    userId: '',
    amount: '',
  });
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [showResults, setShowResults] = React.useState(false);

  const searchUsers = React.useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      // Get non-admin users whose names match the search query
      const { data: { users }, error } = await supabase.auth.admin.listUsers();

      if (error) {
        throw error;
      }

      // Get list of admin user IDs
      const { data: adminUsers } = await supabase
        .from('admin_users')
        .select('id');

      const adminIds = new Set(adminUsers?.map(admin => admin.id) || []);

      // Filter and format results
      const results = users
        .filter(user => 
          !adminIds.has(user.id) && 
          user.raw_app_meta_data?.name?.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10)
        .map(user => ({
          id: user.id,
          name: user.raw_app_meta_data?.name || 'Usuário sem nome'
        }));

      setSearchResults(results);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Erro ao buscar usuários');
    }
  }, []);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchUsers]);

  const loadUserBalance = async (userId: string) => {
    try {
      // Get all referral codes for the user
      const { data: referralCodes, error: referralError } = await supabase
        .from('referral_codes')
        .select('code')
        .eq('user_id', userId);

      if (referralError) throw referralError;

      if (!referralCodes?.length) {
        return 0;
      }

      // Get total commission from sales
      const { data: sales, error: salesError } = await supabase
        .from('sales')
        .select('commission_value')
        .in('referral_code', referralCodes.map(rc => rc.code));

      if (salesError) throw salesError;

      const totalCommission = sales?.reduce((sum, sale) => sum + (sale.commission_value || 0), 0) || 0;

      // Get total withdrawals
      const { data: withdrawals, error: withdrawalsError } = await supabase
        .from('withdrawals')
        .select('amount')
        .eq('user_id', userId)
        .eq('status', 'paid');

      if (withdrawalsError) throw withdrawalsError;

      const totalWithdrawals = withdrawals?.reduce((sum, w) => sum + w.amount, 0) || 0;

      return totalCommission - totalWithdrawals;
    } catch (err) {
      console.error('Error calculating balance:', err);
      throw new Error('Erro ao calcular saldo disponível');
    }
  };

  const handleSelectUser = async (user: UserSearchResult) => {
    try {
      const balance = await loadUserBalance(user.id);
      setSelectedUser({
        id: user.id,
        name: user.name,
        available_balance: balance
      });
      setForm(prev => ({ ...prev, userId: user.id }));
      setSearchTerm(user.name);
      setShowResults(false);
    } catch (err) {
      console.error('Error selecting user:', err);
      setError('Erro ao carregar dados do usuário');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!form.userId || !form.amount) {
      setError('Preencha todos os campos');
      return;
    }

    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Valor inválido');
      return;
    }

    if (!selectedUser) {
      setError('Usuário não selecionado');
      return;
    }

    if (amount > selectedUser.available_balance) {
      setError('Valor maior que o saldo disponível');
      return;
    }

    setIsLoading(true);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Usuário não autenticado');

      const { error: withdrawalError } = await supabase
        .from('withdrawals')
        .insert({
          user_id: form.userId,
          amount: amount,
          status: 'pending',
          created_by: currentUser.id
        });

      if (withdrawalError) throw withdrawalError;

      setSuccess(true);
      setForm({ userId: '', amount: '' });
      setSelectedUser(null);
      setSearchTerm('');
      onSuccess();
    } catch (err) {
      console.error('Error creating withdrawal:', err);
      setError(err instanceof Error ? err.message : 'Erro ao registrar saque');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 text-green-700 rounded-lg">
          Saque registrado com sucesso!
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Usuário
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowResults(true);
                if (!e.target.value) {
                  setSelectedUser(null);
                  setForm(prev => ({ ...prev, userId: '' }));
                }
              }}
              onFocus={() => setShowResults(true)}
              placeholder="Digite o nome do usuário"
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {user.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedUser && (
            <div className="mt-2 p-2 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Saldo disponível: {formatCurrency(selectedUser.available_balance)}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor do Saque
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading || !selectedUser}
        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Save size={20} />
        {isLoading ? 'Registrando...' : 'Registrar Saque'}
      </button>
    </form>
  );
}